import React, { PropsWithChildren } from 'react';
import styled, { css, DefaultTheme, useTheme } from 'styled-components';
import {
  space,
  SpaceProps,
  borders,
  BordersProps,
  display,
  DisplayProps,
  borderColor,
  BorderColorProps
} from 'styled-system';
import { Classes, Dialog as BlueprintDialog, DialogProps as BlueprintDialogProps } from '@blueprintjs/core';
import classNames from 'classnames';

import position from './utils/position';
import size from './utils/size';
import flex from './utils/flex';

import DialogBody from './DialogBody';
import DialogFooter from './DialogFooter';

interface DialogStyledProps extends SpaceProps, BordersProps, DisplayProps, BorderColorProps {
  top?: number;
  maxHeight?: string | number;
  white?: boolean;
  theme?: DefaultTheme;
  role?: string;
}

const DialogStyled = styled(BlueprintDialog)<DialogStyledProps>`
  ${space};
  ${display};
  ${borders};
  ${borderColor};

  ${flex};
  ${position};
  ${size};

  ${({ top, maxHeight }) =>
    !maxHeight &&
    css`
      max-height: calc(100vh - ${(top || 0) * 2 + 48}px);
    `}

  ${({ white, theme }) =>
    white &&
    theme.name === 'light' &&
    css`
      background: ${theme.colors.appBackground};
      .${Classes.DIALOG_BODY} {
        background: ${theme.colors.appBackground};
        border-bottom: ${theme.borders.thin};
      }
      .${Classes.DIALOG_FOOTER} {
        padding-top: 16px;
        background: ${theme.colors.appBackground};
      }
    `}

  .${Classes.DIALOG_HEADER} {
    z-index: 1; /* force the box-shadow to overlay the top of the body */

    h4.${Classes.HEADING} {
      font-size: 16px;
    }
  }
`;

export interface DialogProps
  extends Omit<BlueprintDialogProps, 'portalClassName'>, SpaceProps, BordersProps, DisplayProps, BorderColorProps {
  tracker?: string;
  top?: number;
  maxHeight?: string | number;
  white?: boolean;
  width?: string | number;
  height?: string | number;
  position?: string;
  onClose?: () => void;
  role?: string;
  // allow any additional styled-system / flex / position / size props
  [key: string]: unknown;
}

const DialogInner = ({
  children,
  className,
  tracker,
  canEscapeKeyClose = true,
  ...rest
}: PropsWithChildren<DialogProps>) => {
  const theme = useTheme();
  const dialogClassName = classNames(className, tracker);

  return (
    <DialogStyled
      {...(rest as Record<string, unknown>)}
      role={rest.role || 'dialog'}
      canEscapeKeyClose={canEscapeKeyClose}
      className={dialogClassName}
      portalClassName={theme.name === 'dark' ? 'bp4-dark' : undefined}
    >
      {children}
    </DialogStyled>
  );
};

DialogInner.displayName = 'Dialog';

type DialogComponent = typeof DialogInner & {
  Body: typeof DialogBody;
  Footer: typeof DialogFooter;
};

const Dialog = DialogInner as DialogComponent;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;

export default Dialog;
